import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Shield, AlertTriangle, Clock, ChevronRight, Crosshair, DollarSign, Zap, MapPin, Users, Star, X, CheckCircle, XCircle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const FACTION_COLORS = { cmd: '#8B1A1A', morelli: '#9F7AEA', ddn: '#FF6B35' };
const PRIORITY_COLORS = { CRITICAL: '#DC2626', HIGH: '#F59E0B', MEDIUM: '#3B82F6', LOW: '#6B7280' };

const APPROACH_ICONS = {
  surveillance: { label: 'Surveillance', desc: 'Low risk, high intel', force: 1 },
  street_arrest: { label: 'Street Arrest', desc: 'Quick grab', force: 3 },
  business_raid: { label: 'Business Raid', desc: 'Moderate force', force: 4 },
  residence_raid: { label: 'Residence Raid', desc: 'Home invasion', force: 5 },
  warehouse_raid: { label: 'Warehouse Raid', desc: 'Heavy operation', force: 6 },
  coordinated: { label: 'Coordinated Raid', desc: 'Multi-location', force: 8 },
  interstate: { label: 'Interstate Takedown', desc: 'Maximum force', force: 9 },
};

const PvPRaidPanel = ({ gameId, onBack, onRefreshGame }) => {
  const [targets, setTargets] = useState([]);
  const [approaches, setApproaches] = useState({});
  const [warrants, setWarrants] = useState({});
  const [budget, setBudget] = useState(0);
  const [energy, setEnergy] = useState(100);
  const [evidence, setEvidence] = useState(0);
  const [raidCost, setRaidCost] = useState(5000);
  const [raidEnergy, setRaidEnergy] = useState(20);
  const [loading, setLoading] = useState(true);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [selectedApproach, setSelectedApproach] = useState('street_arrest');
  const [selectedWarrant, setSelectedWarrant] = useState(null);
  const [raidResult, setRaidResult] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('targets');

  const loadTargets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/police/pvp/${gameId}/targets`);
      if (res.ok) {
        const d = await res.json();
        setTargets(d.targets || []);
        setApproaches(d.available_approaches || {});
        setWarrants(d.available_warrants || {});
        setEvidence(d.evidence || 0);
        setEnergy(d.energy || 100);
        setBudget(d.budget || 0);
        setRaidCost(d.raid_cost || 5000);
        setRaidEnergy(d.raid_energy || 20);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [gameId]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/police/pvp/${gameId}/history`);
      if (res.ok) {
        const d = await res.json();
        setHistory(d.raids || []);
      }
    } catch (e) { console.error(e); }
  }, [gameId]);

  useEffect(() => {
    loadTargets();
    loadHistory();
  }, [loadTargets, loadHistory]);

  const executeRaid = async () => {
    if (!selectedTarget || executing) return;
    setExecuting(true);
    setRaidResult(null);
    try {
      const res = await fetch(`${API}/api/police/pvp/${gameId}/raid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_game_id: selectedTarget.game_id,
          approach: selectedApproach,
          warrant_type: selectedWarrant,
        }),
      });
      const d = await res.json();
      if (res.ok) {
        setRaidResult(d);
        loadTargets();
        loadHistory();
        if (onRefreshGame) onRefreshGame();
      } else {
        setRaidResult({ success: false, narrator: d.detail || 'Raid failed to execute.' });
      }
    } catch (e) {
      setRaidResult({ success: false, narrator: 'Network error' });
    }
    setExecuting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pvp-raid-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/40 text-xs tracking-widest hover:text-[#E8E0D0] transition-colors" data-testid="pvp-back-btn">
          BACK
        </button>
        <div className="flex items-center gap-3">
          <span className="font-['Outfit'] text-[9px] tracking-wider text-[#E8E0D0]/30">
            BUDGET: <span className="text-[#C9A84C]">${budget.toLocaleString()}</span>
          </span>
          <span className="font-['Outfit'] text-[9px] tracking-wider text-[#E8E0D0]/30">
            ENERGY: <span className="text-[#3B82F6]">{energy}</span>
          </span>
          <span className="font-['Outfit'] text-[9px] tracking-wider text-[#E8E0D0]/30">
            EVIDENCE: <span className="text-[#10B981]">{evidence}</span>
          </span>
        </div>
      </div>

      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#DC2626]" data-testid="pvp-title">
          OPERATION LOW TIDE — PVP RAIDS
        </h3>
        <p className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/25 mt-1">
          TARGET CRIMINALS. EXECUTE RAIDS. ENFORCE THE LAW.
        </p>
        <p className="font-['Outfit'] text-[10px] text-[#E8E0D0]/40 mt-1">
          Cost per raid: ${raidCost.toLocaleString()} budget + {raidEnergy} energy
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 justify-center">
        {[
          { id: 'targets', label: 'TARGETS', count: targets.length },
          { id: 'history', label: 'RAID LOG', count: history.length },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`font-['Outfit'] text-[10px] tracking-widest px-4 py-1.5 rounded border transition-all ${
              tab === t.id ? 'bg-[#DC2626]/10 border-[#DC2626]/40 text-[#DC2626]' : 'border-[#1A2535] text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60'
            }`}
            data-testid={`pvp-tab-${t.id}`}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {tab === 'targets' && (
        <div className="space-y-3">
          {targets.length === 0 ? (
            <div className="text-center py-10">
              <Shield size={24} className="text-[#E8E0D0]/10 mx-auto mb-3" />
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-sm">No criminal targets available</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/15 text-xs mt-1">Criminals need heat or territories to appear</p>
            </div>
          ) : (
            targets.map((t) => (
              <motion.div
                key={t.game_id}
                layout
                className={`bg-[#0a0e12] border rounded-lg overflow-hidden transition-all cursor-pointer ${
                  selectedTarget?.game_id === t.game_id ? 'border-[#DC2626]/60' : 'border-[#1A2535] hover:border-[#1A2535]/80'
                } ${t.on_cooldown || t.protected ? 'opacity-50' : ''}`}
                onClick={() => !t.on_cooldown && !t.protected && setSelectedTarget(t)}
                data-testid={`pvp-target-${t.game_id}`}
              >
                <div className="p-3 flex items-center gap-3">
                  {/* Priority indicator */}
                  <div className="w-1.5 h-10 rounded-full" style={{ background: PRIORITY_COLORS[t.priority] }} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold truncate">{t.username}</span>
                      <span className="font-['Outfit'] text-[9px] tracking-wider px-1.5 py-0.5 rounded" style={{ color: FACTION_COLORS[t.faction] || '#888', background: (FACTION_COLORS[t.faction] || '#888') + '15' }}>
                        {(t.faction || '').toUpperCase()}
                      </span>
                      <span className="font-['Outfit'] text-[9px] tracking-wider px-1.5 py-0.5 rounded" style={{ color: PRIORITY_COLORS[t.priority], background: PRIORITY_COLORS[t.priority] + '15' }}>
                        {t.priority}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30 flex items-center gap-1">
                        <AlertTriangle size={10} className="text-[#F59E0B]" /> Heat: {t.heat}
                      </span>
                      <span className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30 flex items-center gap-1">
                        <MapPin size={10} /> {t.territories} territories
                      </span>
                      <span className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">
                        Era {t.era} / Lv.{t.level}
                      </span>
                      {t.has_flipped_officers && (
                        <span className="font-['Outfit'] text-[9px] text-[#10B981] flex items-center gap-1">
                          <Users size={10} /> Inside man
                        </span>
                      )}
                    </div>
                  </div>

                  {t.on_cooldown && (
                    <span className="font-['Outfit'] text-[9px] text-[#F59E0B] flex items-center gap-1">
                      <Clock size={10} /> {Math.ceil(t.cooldown_remaining / 60)}m
                    </span>
                  )}
                  {t.protected && (
                    <span className="font-['Outfit'] text-[9px] text-[#3B82F6] flex items-center gap-1">
                      <Shield size={10} /> Protected
                    </span>
                  )}
                  {!t.on_cooldown && !t.protected && (
                    <ChevronRight size={14} className="text-[#E8E0D0]/20" />
                  )}
                </div>

                {/* Expanded raid options */}
                <AnimatePresence>
                  {selectedTarget?.game_id === t.game_id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-[#1A2535]"
                    >
                      <div className="p-3 space-y-3">
                        {/* Approach selection */}
                        <div>
                          <span className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/30 block mb-2">SELECT APPROACH</span>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                            {Object.entries(approaches).map(([key, a]) => {
                              const info = APPROACH_ICONS[key] || { label: a.name, desc: '', force: a.force };
                              return (
                                <button
                                  key={key}
                                  onClick={(e) => { e.stopPropagation(); setSelectedApproach(key); }}
                                  className={`text-left p-2 rounded border transition-all ${
                                    selectedApproach === key ? 'border-[#DC2626]/50 bg-[#DC2626]/5' : 'border-[#1A2535] hover:border-[#1A2535]/80'
                                  }`}
                                  data-testid={`approach-${key}`}
                                >
                                  <span className="font-['Outfit'] text-[9px] text-[#E8E0D0] block">{info.label}</span>
                                  <span className="font-['Outfit'] text-[8px] text-[#E8E0D0]/25">{info.desc}</span>
                                  <div className="flex gap-1 mt-1">
                                    {[...Array(Math.ceil(a.force / 2))].map((_, i) => (
                                      <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />
                                    ))}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Warrant (optional) */}
                        {Object.keys(warrants).length > 0 && (
                          <div>
                            <span className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/30 block mb-2">WARRANT (OPTIONAL — BOOSTS SUCCESS)</span>
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedWarrant(null); }}
                                className={`font-['Outfit'] text-[9px] px-2 py-1 rounded border transition-all ${
                                  !selectedWarrant ? 'border-[#E8E0D0]/30 text-[#E8E0D0]' : 'border-[#1A2535] text-[#E8E0D0]/30'
                                }`}
                              >
                                NO WARRANT
                              </button>
                              {Object.entries(warrants).map(([key, w]) => (
                                <button
                                  key={key}
                                  onClick={(e) => { e.stopPropagation(); setSelectedWarrant(key); }}
                                  className={`font-['Outfit'] text-[9px] px-2 py-1 rounded border transition-all ${
                                    selectedWarrant === key ? 'border-[#C9A84C]/50 text-[#C9A84C]' : 'border-[#1A2535] text-[#E8E0D0]/30'
                                  }`}
                                  data-testid={`warrant-${key}`}
                                >
                                  {w.name} ({w.evidence_req} ev)
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Execute */}
                        <button
                          onClick={(e) => { e.stopPropagation(); executeRaid(); }}
                          disabled={executing || budget < raidCost || energy < raidEnergy}
                          className={`w-full py-2.5 rounded font-['Outfit'] text-xs tracking-widest transition-all flex items-center justify-center gap-2 ${
                            executing ? 'bg-[#DC2626]/20 text-[#DC2626]/60' :
                            budget < raidCost || energy < raidEnergy ? 'bg-[#1A2535] text-[#E8E0D0]/20 cursor-not-allowed' :
                            'bg-[#DC2626]/15 border border-[#DC2626]/40 text-[#DC2626] hover:bg-[#DC2626]/25'
                          }`}
                          data-testid="execute-pvp-raid-btn"
                        >
                          {executing ? (
                            <><div className="w-3 h-3 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" /> EXECUTING RAID...</>
                          ) : (
                            <><Crosshair size={14} /> EXECUTE RAID — ${raidCost.toLocaleString()} + {raidEnergy} ENERGY</>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-2">
          {history.length === 0 ? (
            <div className="text-center py-10">
              <Target size={24} className="text-[#E8E0D0]/10 mx-auto mb-3" />
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-sm">No raids executed yet</p>
            </div>
          ) : (
            history.map((r, i) => (
              <div key={i} className={`p-3 rounded-lg border ${r.success ? 'bg-[#10B981]/5 border-[#10B981]/20' : 'bg-[#DC2626]/5 border-[#DC2626]/20'}`} data-testid={`raid-history-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {r.success ? <CheckCircle size={12} className="text-[#10B981]" /> : <XCircle size={12} className="text-[#DC2626]" />}
                    <span className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{r.target_name}</span>
                    <span className="font-['Outfit'] text-[9px] px-1.5 py-0.5 rounded" style={{ color: FACTION_COLORS[r.target_faction] || '#888', background: (FACTION_COLORS[r.target_faction] || '#888') + '15' }}>
                      {(r.target_faction || '').toUpperCase()}
                    </span>
                  </div>
                  <span className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">{new Date(r.timestamp).toLocaleString()}</span>
                </div>
                <p className="font-['Outfit'] text-[10px] text-[#E8E0D0]/40">{r.approach} {r.warrant ? `+ ${r.warrant}` : ''}</p>
                {r.consequences?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {r.consequences.map((c, j) => (
                      <span key={j} className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#DC2626]/10 text-[#DC2626]">
                        {c.type === 'cash_seized' ? `$${c.amount?.toLocaleString()} seized` : c.type === 'crew_arrested' ? `Crew arrested (${c.hours}h)` : c.type === 'territory_seized' ? `Territory: ${c.name}` : c.type}
                      </span>
                    ))}
                  </div>
                )}
                {r.target_warned && <span className="font-['Outfit'] text-[8px] text-[#F59E0B] mt-1 block">Target was warned by compromised officer</span>}
              </div>
            ))
          )}
        </div>
      )}

      {/* Raid Result Modal */}
      <AnimatePresence>
        {raidResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setRaidResult(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className={`max-w-md w-full rounded-lg border p-6 ${
                raidResult.success ? 'bg-[#0a0e12] border-[#10B981]/30' : 'bg-[#0a0e12] border-[#DC2626]/30'
              }`}
              onClick={(e) => e.stopPropagation()}
              data-testid="raid-result-modal"
            >
              <div className="text-center mb-4">
                {raidResult.success ? (
                  <CheckCircle size={40} className="text-[#10B981] mx-auto mb-2" />
                ) : (
                  <XCircle size={40} className="text-[#DC2626] mx-auto mb-2" />
                )}
                <h3 className="font-['Cormorant_Garamond'] text-xl font-bold" style={{ color: raidResult.success ? '#10B981' : '#DC2626' }}>
                  {raidResult.success ? 'RAID SUCCESSFUL' : 'RAID FAILED'}
                </h3>
                {raidResult.target && (
                  <p className="font-['Outfit'] text-[10px] text-[#E8E0D0]/40 mt-1">
                    {raidResult.approach} on {raidResult.target} ({(raidResult.target_faction || '').toUpperCase()})
                  </p>
                )}
              </div>

              <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm mb-4 text-center">
                {raidResult.narrator}
              </p>

              {raidResult.consequences?.length > 0 && (
                <div className="space-y-1.5 mb-4">
                  <span className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/30">CONSEQUENCES</span>
                  {raidResult.consequences.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded bg-[#111820] border border-[#1A2535]">
                      {c.type === 'cash_seized' && <DollarSign size={12} className="text-[#C9A84C]" />}
                      {c.type === 'crew_arrested' && <Users size={12} className="text-[#DC2626]" />}
                      {c.type === 'territory_seized' && <MapPin size={12} className="text-[#3B82F6]" />}
                      <span className="font-['Outfit'] text-[#E8E0D0]/60 text-xs">
                        {c.type === 'cash_seized' && `$${c.amount?.toLocaleString()} seized`}
                        {c.type === 'crew_arrested' && `Crew member arrested (${c.hours}h lockup)`}
                        {c.type === 'territory_seized' && `Territory seized: ${c.name}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {raidResult.rewards && raidResult.success && (
                <div className="p-2 rounded bg-[#10B981]/5 border border-[#10B981]/15 mb-4">
                  <span className="font-['Outfit'] text-[9px] tracking-widest text-[#10B981]/50 block mb-1">YOUR REWARDS</span>
                  <div className="flex flex-wrap gap-3">
                    {raidResult.rewards.xp > 0 && <span className="font-['Outfit'] text-[10px] text-[#10B981]">+{raidResult.rewards.xp} XP</span>}
                    {raidResult.rewards.cash > 0 && <span className="font-['Outfit'] text-[10px] text-[#C9A84C]">+${raidResult.rewards.cash.toLocaleString()}</span>}
                    {raidResult.rewards.evidence > 0 && <span className="font-['Outfit'] text-[10px] text-[#3B82F6]">+{raidResult.rewards.evidence} evidence</span>}
                  </div>
                </div>
              )}

              {raidResult.target_warned && (
                <div className="p-2 rounded bg-[#F59E0B]/5 border border-[#F59E0B]/15 mb-4">
                  <span className="font-['Outfit'] text-[10px] text-[#F59E0B]">Target was warned by a compromised officer — success reduced</span>
                </div>
              )}

              <button
                onClick={() => setRaidResult(null)}
                className="w-full py-2 rounded font-['Outfit'] text-xs tracking-widest border border-[#1A2535] text-[#E8E0D0]/50 hover:text-[#E8E0D0] transition-colors"
                data-testid="close-raid-result"
              >
                DISMISS
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PvPRaidPanel;
